import React, { useState } from 'react';
import { PlanSwitchDirection } from '../../../types/subscription.types';

interface PlanSwitchModalPlan {
  display_name: string;
  price_display: string;
}

interface PlanSwitchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  currentPlan: PlanSwitchModalPlan | undefined;
  targetPlan: PlanSwitchModalPlan | undefined;
  direction: PlanSwitchDirection;
  isTrial: boolean;
  trialEndLabel?: string;
}

const PlanSwitchModal: React.FC<PlanSwitchModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentPlan,
  targetPlan,
  direction,
  isTrial,
  trialEndLabel,
}) => {
  const [confirming, setConfirming] = useState(false);

  if (!isOpen || !targetPlan) return null;

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  };

  const isTrialUpgrade = isTrial && direction === 'upgrade';
  const isTrialDowngrade = isTrial && direction === 'downgrade';

  const title = isTrialUpgrade ? `Start ${targetPlan.display_name} now` : `Switch to ${targetPlan.display_name}`;

  const primaryLabel = isTrialUpgrade
    ? `Start ${targetPlan.display_name} now`
    : `Switch to ${targetPlan.display_name}`;

  const primaryButtonClass = isTrialUpgrade || direction === 'upgrade'
    ? 'bg-primary text-white hover:bg-primary-hover'
    : 'bg-map-green text-white hover:bg-map-green-hover';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary/40 px-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl">
        <div className="px-7 pt-7">
          <h3 className="text-xl font-extrabold text-secondary">{title}</h3>

          {isTrialUpgrade && (
            <p className="mt-2.5 text-[14.5px] leading-relaxed text-gray-600">
              Start {targetPlan.display_name} now — you&apos;ll be charged{' '}
              <b className="font-bold text-secondary">{targetPlan.price_display} today</b>, your limits jump
              immediately, and{' '}
              <b className="font-bold text-primary">this ends your free trial</b>.
            </p>
          )}

          {isTrialDowngrade && (
            <p className="mt-2.5 text-[14.5px] leading-relaxed text-gray-600">
              Switch to {targetPlan.display_name} — you keep your free trial until{' '}
              <b className="font-bold text-secondary">{trialEndLabel ?? 'it ends'}</b>, then your first charge is{' '}
              <b className="font-bold text-secondary">{targetPlan.price_display}/mo</b> instead of{' '}
              {currentPlan?.price_display ?? ''}/mo. Your saved results stay.
            </p>
          )}

          {!isTrial && (
            <p className="mt-2.5 text-[14.5px] leading-relaxed text-gray-600">
              Your plan updates immediately and a{' '}
              {direction === 'upgrade' ? 'prorated charge applies to your next invoice' : 'prorated credit applies to your next invoice'}.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2.5 px-7 pb-7 pt-5">
          <button
            onClick={handleConfirm}
            disabled={confirming}
            className={`w-full rounded-xl py-3.5 text-sm font-bold disabled:opacity-60 ${primaryButtonClass}`}
          >
            {confirming ? 'Switching…' : primaryLabel}
          </button>
          <button
            onClick={onClose}
            disabled={confirming}
            className="w-full rounded-xl border-[1.5px] border-gray-200 bg-white py-3 text-sm font-semibold text-gray-500 hover:bg-gray-50 disabled:opacity-60"
          >
            {isTrial ? `Keep ${currentPlan?.display_name ?? 'my plan'}` : 'Cancel'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlanSwitchModal;
